import { QueryDataSource } from '@softapps/core';
import { PageFilterDataService, PageFilterValuePage } from './page-filter-data.service';
import { filterEmpty, getValue, isDefinedNotEmpty, Path, QueryBuilder, QueryFilter } from '@softline/core';
import { getQueryColumn } from '../../../helper/get-query-column.function';
import { FilterItemConfig } from '../../../types/filter-config';
import { Signal } from '@angular/core';

export class PageFilterQueryDataService<T extends object> extends PageFilterDataService<T> {

  constructor(private dataSource: QueryDataSource<T>, private baseFilter?: Signal<QueryFilter<T>>) {
    super();
  }

  override async loadCount(items: FilterItemConfig<T>[], queryFilter: QueryFilter<T>): Promise<Record<Path<T>, number>> {
    const queryBuilder = new QueryBuilder<T>()
      .end(1);
    const queryColumns = items.map(item => getQueryColumn(item));
    for(const column of queryColumns)
      queryBuilder.column(column, {agg: 'totalcount' as any});
    queryBuilder.filter(queryFilter);
    const result = await this.dataSource.load({ query: queryBuilder.build() });
    const returnValue = {} as Record<Path<T>, number>;
    for(const column of queryColumns)
      returnValue[column] = getValue(result.data[0], `${column}:totalcount`);
    return returnValue;
  }

  override async loadItems(item: FilterItemConfig<T>, filter: QueryFilter<T> | null, start: number, end: number, search: string): Promise<PageFilterValuePage<T>> {
    const queryBuilder = new QueryBuilder<T>()
      .start(start)
      .end(end)
    if(item.type === 'entity')
      queryBuilder.column(item.field + '.id' as Path<T>)
    else
      queryBuilder.column(item.field)

    if(item.type === 'entity'){
      for(const additionalField of item.additionalFields)
        queryBuilder.column(item.field + '.' +additionalField as Path<T>)
    }
    let searchFilter: QueryFilter<T> | null = null;
    if(isDefinedNotEmpty(search)) {
      searchFilter = {
        type: 'conditions',
        operator: 'OR',
        filters: []
      }
      if(item.type === 'entity' && !Number.isNaN(Number(search))) {
        searchFilter.filters.push({
          type: 'numberEquals',
          name: item.field + '.id' as Path<T>,
          value: Number(search)
        } as QueryFilter<T>)
      }
      else if (item.type === 'string') {
        searchFilter.filters.push({
          type: 'stringContains',
          name: item.field,
          value: search
        } as QueryFilter<T>)
      }
      if(item.type === 'entity'){
        for(const additionalField of item.additionalFields)
          searchFilter.filters.push({
            type: 'stringContainsIgnoreCase',
            name: item.field + '.' + additionalField as Path<T>,
            value: search
          } as QueryFilter<T>)
      }
    }
    if(searchFilter)
      queryBuilder.filter(searchFilter);
    if(item.optionFilter)
      queryBuilder.filter(item.optionFilter);
    if(filter)
      queryBuilder.filter(filter);
    if(this.baseFilter){
      const baseFilter = this.baseFilter();
      if(baseFilter)
        queryBuilder.filter(baseFilter);
    }

    let sortField: string = item.field;
    if(item.type === 'entity')
      sortField = (sortField + '.' + item.sortField) as Path<T>;
    queryBuilder.sort(sortField as Path<T>, 'ASC');
    const query = queryBuilder.build();
    const result = await this.dataSource.load({query});
    const rows = result.data ?? [];
    // rawCount counts the rows the backend sent; values only what is renderable, so an empty
    // list means "nothing to show" instead of "a window of null column values"
    const values = filterEmpty(rows)
      .map(o => getValue(o, item.field))
      .filter(value => isDefinedNotEmpty(value));
    return { values, rawCount: rows.length };
  }
}
